
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Database, Calendar, Database as DatabaseIcon } from "lucide-react";
import { format } from "date-fns";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { SystemStatus } from "@/components/SystemStatus";
import { useChatStats, useSelfEvaluationStats, useExperimentTimeline, useMemoryLengthData } from "@/hooks/useDoveData";

export const SystemStatusSection = () => {
  const { data: chatData, isLoading: chatLoading } = useChatStats();
  const { data: selfEvalStats, isLoading: selfEvalLoading } = useSelfEvaluationStats();
  const { data: timelineData, isLoading: timelineLoading } = useExperimentTimeline();
  const { data: memoryLengthData, isLoading: memoryLengthLoading } = useMemoryLengthData();

  // Use fallback data or API data for system status
  const chatStatsData = chatData || [];
  const selfEvaluationPercentage = selfEvalStats?.percentage || 68;
  const selfEvaluationCompleted = selfEvalStats?.completed || 680;
  const daysLeftToCompletion = timelineData?.daysLeft || 42;
  const totalDays = timelineData?.totalDays || 90;
  const memoryData = memoryLengthData || [];

  return (
    <section id="system-status" className="py-24 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center text-primary">System Status Dashboard</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {/* Chats Card */}
          <Card className="bg-white/80 backdrop-blur-sm shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-medium">Chats (Last 7 Days)</CardTitle>
              <Database className="h-5 w-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chatStatsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="chats" fill="#6366f1" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Memory Length Chart */}
          <Card className="bg-white/80 backdrop-blur-sm shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-medium">Internal State Length</CardTitle>
              <DatabaseIcon className="h-5 w-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                {memoryLengthLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-muted-foreground">Loading memory data...</span>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={memoryData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), 'MMM dd')}
                      />
                      <YAxis 
                        domain={['auto', 'auto']}
                        tickFormatter={(value) => {
                          if (value >= 1000) {
                            return `${(value / 1000).toFixed(0)}k`;
                          }
                          return value;
                        }}
                      />
                      <Tooltip 
                        labelFormatter={(label) => format(new Date(label), 'PPP')}
                        formatter={(value) => [`${value} tokens`, 'Memory Size']}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="length" 
                        stroke="#8884d8" 
                        strokeWidth={2}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Experiment Timeline Card */}
          <Card className="bg-white/80 backdrop-blur-sm shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-medium">Experiment Timeline</CardTitle>
              <Calendar className="h-5 w-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="space-y-6 mt-6">
                <div className="text-center space-y-3">
                  <div>
                    <span className="text-4xl font-bold text-primary">{daysLeftToCompletion}</span>
                    <p className="text-sm text-muted-foreground mt-1">Days left to completion</p>
                  </div>
                  <div className="mt-4">
                    <span className="text-2xl font-bold text-indigo-500">{selfEvaluationPercentage}%</span>
                    <p className="text-sm text-muted-foreground">Percentage complete</p>
                  </div>
                </div>
                <div className="relative pt-6">
                  <div className="w-full h-2 bg-gray-200 rounded">
                    <div 
                      className="h-2 bg-primary rounded" 
                      style={{
                        width: `${100 - daysLeftToCompletion / totalDays * 100}%`
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                    <span>Start</span>
                    <span>Midpoint</span>
                    <span>Completion</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* System Status Table */}
        <div className="my-10">
          <SystemStatus />
        </div>
      </div>
    </section>
  );
};
